import React, { useEffect, useState } from "react";
import { ShoppingCart, X, Plus, Minus, Home } from "lucide-react";
import Cta from "../components/Cta";
import { BASE, IMAGEBASE, tokenService } from "../lib/service";
import Modal from "../components/ui/Modal";
import { CloseCircle } from "iconsax-react";
import { useNavigate } from "react-router-dom";

const Customer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState("");
  const navigate = useNavigate();

  const HandleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    getProfile();
    getProducts();
  }, []);

  const token = tokenService?.getToken();

  async function getProfile() {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE}/customer-profile`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setProfile(data?.data?.full_name);

     
    } catch (error) {
      setMessage(error?.error ? error?.error : "Login Failed");
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  }

  async function getProducts() {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE}/products`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer NGN{token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setProducts(data);
      } else {
        setMessage(data?.error || "Failed to load products");
        setStatus("error");
      }
    } catch (error) {
      setMessage(error?.message || "Failed to load products");
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  }

  const addToCart = (product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const handleCheckout = () => {
    // Implement your checkout logic here
    setIsCartOpen(false);
    setOpen(true);
  };

  return (
    <div className="md:p-[20px] p-[10px] bg-[#F2F2F2] min-h-screen">
      {/* Navigation Bar */}
      <div className="flex justify-between items-center mb-6 p-4 bg-white rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate("/");
              tokenService.removeToken();
            }}
          >
            <Home />
          </button>
          <p className="text-base md:text-lg font-bold text-gray-600">
            Hello, <span className="text-[#26ae5f]">{profile}</span>
          </p>
        </div>

        {/* Cart Icon with Badge */}
        <div className="relative">
          <button
            onClick={() => setIsCartOpen(!isCartOpen)}
            className="p-2 rounded-full hover:bg-gray-100 relative"
          >
            <ShoppingCart size={24} className="text-gray-700" />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cart.reduce((total, item) => total + item.quantity, 0)}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div>
        <p className="text-base md:text-lg font-bold text-gray-600 mb-4">
          Products
        </p>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>Loading products...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((product) => (
              <div
                key={product.id}
                className="w-full rounded-lg overflow-hidden shadow-lg bg-white hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative">
                  <img
                    src={IMAGEBASE + product?.image_url}
                    alt={product.name}
                    className="w-full h-64 object-cover"
                  />
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-lg text-gray-800 mb-1">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-2">NGN{product?.price}</p>

                  <button
                    onClick={() => addToCart(product)}
                    className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md font-medium transition-colors duration-200 bg-green-600 hover:bg-green-700 text-white`}
                  >
                    <ShoppingCart size={18} />
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Sidebar */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsCartOpen(false)}
          ></div>

          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
            <div className="flex flex-col h-full">
              {/* Cart Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-bold">Your Cart</h2>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4">
                {cart.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    Your cart is empty
                  </p>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center border-b pb-4"
                      >
                        <img
                          src={IMAGEBASE + item?.image_url}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />

                        <div className="ml-4 flex-1">
                          <h3 className="font-medium">{item.name}</h3>
                          <p className="text-gray-600">NGN{item.price}</p>

                          <div className="flex items-center mt-2">
                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                              className="p-1 rounded hover:bg-gray-100"
                            >
                              <Minus size={16} />
                            </button>

                            <span className="mx-2 w-8 text-center">
                              {item.quantity}
                            </span>

                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                              className="p-1 rounded hover:bg-gray-100"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="ml-4">
                          <p className="font-medium">
                            NGN{(item.price * item.quantity).toFixed(2)}
                          </p>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-700 mt-1"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cart Footer */}
              <div className="p-4 border-t">
                <div className="flex justify-between mb-4">
                  <span className="font-bold">Total:</span>
                  <span className="font-bold">
                    NGN{calculateTotal().toFixed(2)}
                  </span>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={cart.length === 0}
                  className={`w-full py-2 px-4 rounded-md font-medium transition-colors duration-200 NGN{
                    cart.length > 0
                      ? " bg-green-500 hover:bg-green-700 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={open} onClose={HandleClose}>
        <div className="inline-block relative bg-[white] mt-10  p-[18px] md:p-[24px] xl:p-[32px] rounded-[18px] md:rounded-[20px] lg:rounded-[24px] overflow-hidden text-left align-bottom transition-all transform   shadow-xl sm:my-8 sm:align-middle w-full min-w-[360px] md:min-w-[450px] md:max-w-[550px]">
          <button onClick={HandleClose} className="absolute top-3 right-2">
            <CloseCircle color="gray" size={16} />
          </button>

          <p className="font-semibold text-center mb-4">
            Pay a total of NGN{calculateTotal().toFixed(2)} to the account below
            to complete this purchase.
          </p>
          <p>
            Bank Name: <span className="font-medium">First Bank PLC</span>{" "}
          </p>
          <p>
            Bank Account: <span className="font-medium">2041836374</span>{" "}
          </p>
          <p>
            Account Name:{" "}
            <span className="font-medium">
              De Ashymai Global Investment LTD
            </span>
          </p>

          <p className="text-sm mt-7 text-center">Thanks for choosing us!</p>
        </div>
      </Modal>
    </div>
  );
};

export default Customer;
